import { ArticlePreviewView, GrowthDashboardTopBarActions } from '@elva/ui';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AsyncBoundary } from '@/components/feedback';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { viewProps } from '@/data/viewProps';
import { useNotify } from '@/data/notifications';
import { getArticleImageSignedUrl } from '../api';
import { useArticleDetail, useArticleMutations } from '../hooks/useArticles';

export function ArticlePreviewPage() {
  const { articleId = '' } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();

  usePageTopBar({
    actions: <GrowthDashboardTopBarActions />,
  });

  const { data, isPending, isError, error, refetch, isRefetching, isFetching } =
    useArticleDetail(articleId);
  const { handoff, regenerate } = useArticleMutations();

  const handleCopyText = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        notify.success('Copied to clipboard');
      } catch (copyError) {
        notify.error(copyError, 'Copy failed');
      }
    },
    [notify],
  );

  const handleDownloadImage = useCallback(
    async (imageId: number) => {
      try {
        const url = await getArticleImageSignedUrl(articleId, imageId);
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch (downloadError) {
        notify.error(downloadError, 'Image download failed');
      }
    },
    [articleId, notify],
  );

  return (
    <AsyncBoundary
      loading={isPending}
      error={data === undefined && isError ? error : undefined}
      onRetry={refetch}
      retrying={isRefetching}
    >
      <ArticlePreviewView
        {...viewProps({
          articleId,
          article: data,
          loading: isFetching || handoff.isPending || regenerate.isPending,
          onBack: () => navigate('/content'),
          onCopyText: handleCopyText,
          onDownloadImage: handleDownloadImage,
          onHandoff: () => handoff.mutate(articleId),
          onRegenerate: () => regenerate.mutate(articleId),
        })}
      />
    </AsyncBoundary>
  );
}
